import type {
  ApiResponse, ContactRequestPatch, ContactRequestRow, ContactRequestStatus, FunnelDay, SiteMedia, SiteProofRaw,
  OrderPatch, OrderRow, OrderStatus, OrderWeddingOption, PrivacyEraseResult, PrivacyLookup, SiteSection, SiteSettings,
  AddonAdminPatch, WeddingAddonRow, WeddingAddonStatus, AdminOrderCreate, OrderImpact,
  SalesLedgerEntry, SalesLedgerKind,
} from "@wedding/shared";

/** Form girdisi: varsayılanlı alanlar isteğe bağlı (Zod `.default`). */
export type AdminOrderCreateInput = Omit<AdminOrderCreate, "phone" | "billingAddress" | "taxId" | "adminNote" | "quantity"> &
  Partial<Pick<AdminOrderCreate, "phone" | "billingAddress" | "taxId" | "adminNote" | "quantity">>;
import type { QrAnalytics } from "@wedding/qr-analytics";

/**
 * Admin API client — always uses relative paths.
 * In production, admin.wedding.mertbal.com proxies /api/* to the Worker.
 * In dev, Vite proxy forwards /api/* to localhost:8787.
 * CF Access provides authentication at the edge.
 */

async function request<T>(
  base: string,
  path: string,
  options?: RequestInit,
): Promise<ApiResponse<T>> {
  const url = path && path !== "/" ? `${base}${path}` : base;
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options?.headers },
    ...options,
  });
  const text = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new Error(`Sunucu hatası: ${res.status} ${res.statusText}`);
  }
  if (!res.ok) throw new Error(json.error ?? `Request failed: ${res.status}`);
  return json;
}

export interface ActivityItem {
  type: "upload" | "upload_pending" | "like" | "comment";
  id: string;
  guest_id: string;
  guest_name: string | null;
  media_id: string;
  media_r2_key: string;
  media_kind: "image" | "video";
  content: string | null;
  created_at: string;
}

/** Per-wedding admin API */
export const createAdminApi = (slug: string) => {
  const base = `/api/admin/w/${slug}`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) =>
      request<T>(base, path, { method: "DELETE" }),
    getActivity: (limit = 10) =>
      request<ActivityItem[]>(base, `/activity?filter=all&limit=${limit}`),
  };
};

/** Admin wedding CRUD API */
export const adminWeddingsApi = (() => {
  const base = `/api/admin/weddings`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  };
})();

/** Per-wedding background upload/delete (admin) */
export const createAdminBgApi = (slug: string) => ({
  upload: async (slot: string, file: File): Promise<{ url: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/admin/weddings/${slug}/backgrounds/${slot}`, {
      method: "POST", credentials: "include", body: fd,
    });
    const json = await res.json() as { ok: boolean; data?: { url: string }; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Upload failed");
    return json.data!;
  },
  delete: (slot: string) =>
    request<void>(`/api/admin/weddings/${slug}`, `/backgrounds/${slot}`, { method: "DELETE" }),
});

/**
 * Admin-side join-theme API — mirrors the organizer's `createJoinThemeApi`
 * shape (so it satisfies the shared `JoinThemeApi` contract) but hits the admin
 * per-wedding endpoint. Same worker handlers, admin auth → audit attributes the
 * change to the admin.
 */
export const createAdminJoinThemeApi = (slug: string) => {
  const base = `/api/admin/w/${slug}/join-theme`;
  return {
    updateConfig: (body: {
      layoutId?: string;
      config?: Record<string, unknown>;
      preset?: string;
      palette?: string;
      fontPair?: string;
    }) => request<void>(base, "/config/join", { method: "PATCH", body: JSON.stringify(body) }),

    uploadAsset: async (file: File, opts: { layoutId: string; slot: string }): Promise<{ url: string; key: string }> => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("layoutId", opts.layoutId);
      fd.append("slot", opts.slot);
      const res = await fetch(`${base}/join-asset`, { method: "POST", credentials: "include", body: fd });
      const json = (await res.json()) as { ok: boolean; data?: { url: string; key: string }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    deleteAsset: (key: string) =>
      request<void>(base, "/join-asset", { method: "DELETE", body: JSON.stringify({ key }) }),

    uploadHero: async (file: File): Promise<{ url: string }> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${base}/join-hero`, { method: "POST", credentials: "include", body: fd });
      const json = (await res.json()) as { ok: boolean; data?: { url: string }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    deleteHero: () => request<void>(base, "/join-hero", { method: "DELETE" }),

    addGalleryImage: async (file: File): Promise<{ url: string; index: number }> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${base}/join-images`, { method: "POST", credentials: "include", body: fd });
      const json = (await res.json()) as { ok: boolean; data?: { url: string; index: number }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    deleteGalleryImage: (index: number) =>
      request<void>(base, `/join-images/${index}`, { method: "DELETE" }),

    uploadFrame: async (blob: Blob, index: number): Promise<void> => {
      const fd = new FormData();
      fd.append("file", blob, `frame-${index}.jpg`);
      const res = await fetch(`${base}/join-frames/${index}`, { method: "POST", credentials: "include", body: fd });
      const json = (await res.json()) as { ok: boolean; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Frame upload failed");
    },

    setFrameCount: (count: number) =>
      request<void>(base, "/join-frame-count", { method: "PATCH", body: JSON.stringify({ count }) }),

    deleteFrames: () => request<void>(base, "/join-frames", { method: "DELETE" }),
  };
};

/** Admin user management API */
export const adminUsersApi = (() => {
  const base = `/api/admin/users`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  };
})();

/** Admin plans management API */
export const adminPlansApi = (() => {
  const base = `/api/admin/plans`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) =>
      request<T>(base, path, { method: "DELETE" }),
  };
})();

/** Admin audit log API */
export const adminAuditApi = (() => {
  const base = `/api/admin/audit`;
  return {
    get: <T>(path: string) => request<T>(base, path),
  };
})();

export interface KvBreakdownRow { category: string; op: string; n: number; avg_ms: number; }
export interface KvIpRow        { ip: string; sub: string; hits: number; }
export interface KvRouteRow     { route: string; n: number; }
export interface KvMetricsData  {
  breakdown: KvBreakdownRow[];
  topIps: KvIpRow[];
  topRoutes: KvRouteRow[];
}

/** Payload shape is owned by the shared page package (single source of truth). */
export type { QrAnalytics } from "@wedding/qr-analytics";

/**
 * Admin-side QR analytics API — mirrors the organizer's `createAnalyticsApi`
 * shape (so it satisfies the shared `QrAnalyticsApi` contract) but hits the
 * admin per-wedding endpoint. Same worker handlers, admin auth.
 */
export const createAdminAnalyticsApi = (slug: string) => {
  const base = `/api/admin/w/${slug}/analytics`;
  return {
    getQr: () => request<QrAnalytics>(base, "/qr"),
    /** Fetch the CSV export as a blob. `lang` localizes the CSV section headers. */
    exportCsv: async (lang?: string): Promise<Blob> => {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : "";
      const res = await fetch(`${base}/qr/export.csv${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      return res.blob();
    },
  };
};

/** Admin maintenance dashboard API */
export const adminMaintenanceApi = {
  getStats: () =>
    request("/api/admin/maintenance/groups", "/stats"),
  getGroups: (params?: URLSearchParams) =>
    request(
      "/api/admin/maintenance",
      params?.toString() ? `/groups?${params.toString()}` : "/groups",
    ),
  getGroup: (fingerprint: string) =>
    request("/api/admin/maintenance/groups", `/${fingerprint}`),
  getEvents: (fingerprint: string, cursor?: string) =>
    request("/api/admin/maintenance/groups", `/${fingerprint}/events${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),
  patchGroup: (fingerprint: string, body: { is_resolved: boolean; resolution_note?: string }) =>
    request("/api/admin/maintenance/groups", `/${fingerprint}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  purge: () =>
    request("/api/admin/maintenance", "/purge", { method: "DELETE" }),
  getKvMetrics: (window: "1h" | "24h" | "7d") =>
    request<KvMetricsData>("/api/admin/maintenance", `/kv-metrics?window=${window}`),
};

/** Runtime-configurable financial caps + kill switches (admin). */
export interface FinancialCaps {
  verifyDailyCap: number;
  smsSegmentDailyCap: number;
  smsPreviewDailyCap: number;
  smsRoundRecipientHardCap: number;
  smsManualSegmentMaxGuests: number;
  smsTrPriceUsd: number;
  maxImageBytes: number;
  maxVideoDirectBytes: number;
  emailDailyCap: number;
  qrScanVisitorDailyCap: number;
  /** Düğün başına 24 saatlik self-register (QR/join) tavanı. */
  selfRegisterDailyCap: number;
  /** Alıcı sayısı bunu AŞAN kampanya admin onayı bekler (047). */
  smsApprovalThreshold: number;
  verifyEnabled: boolean;
  uploadsEnabled: boolean;
  imageTransformEnabled: boolean;
  /** ⚠️ Diğer switch'lerin AKSİNE: true = KAPI AKTİF (gönderim kısıtlı). */
  smsApprovalEnabled: boolean;
  /** Misafirin kendi kaldırdığı medya organizatöre salt okunur görünsün mü (054; varsayılan kapalı). */
  organizerSeesGuestDeletedMedia: boolean;
}
export type FinancialOverrides = Partial<FinancialCaps>;
export interface FinancialMonitoring {
  verifyUsedToday: number;
  verifyResetAt: string | null;
  smsSegmentsUsedToday: number;
  spendBreaker: { tripped: boolean; trippedAt?: string; reason?: string };
}
export interface FinancialConfigData {
  effective: FinancialCaps;
  overrides: FinancialOverrides;
  monitoring: FinancialMonitoring;
}
/** Partial patch — a number/boolean sets an override; `null` clears it (reverts to default). */
export type FinancialConfigPatch = {
  [K in keyof FinancialCaps]?: FinancialCaps[K] | null;
};

/**
 * Admin Control Center API.
 * NOTE: the worker route stays `/api/admin/notifications` on purpose — admin Pages and
 * the Worker deploy independently, so renaming the path would 404 during the deploy gap.
 */
export const adminControlCenterApi = {
  getConfig: () =>
    request("/api/admin/notifications", "/config"),
  patchConfig: (body: { sms_enabled?: boolean; email_enabled?: boolean; sms_campaigns_enabled?: boolean }) =>
    request("/api/admin/notifications", "/config", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  getStats: () =>
    request("/api/admin/notifications", "/stats"),
  getSmsCampaignsStats: () =>
    request("/api/admin/notifications", "/sms-campaigns-stats"),
  getSmsSpend: () =>
    request("/api/admin/notifications", "/sms-spend"),
  resetSmsSpendBreaker: () =>
    request("/api/admin/notifications", "/sms-spend/reset-breaker", { method: "POST" }),
  getLogs: (params?: URLSearchParams) =>
    request("/api/admin/notifications", params?.toString() ? `/logs?${params.toString()}` : "/logs"),
  getFinancialConfig: () =>
    request<FinancialConfigData>("/api/admin/notifications", "/financial-config"),
  patchFinancialConfig: (body: FinancialConfigPatch) =>
    request<{ effective: FinancialCaps; overrides: FinancialOverrides }>(
      "/api/admin/notifications",
      "/financial-config",
      { method: "PATCH", body: JSON.stringify(body) },
    ),
};

/**
 * "Satış & Site" — landing'in yönetilebilir içeriği (`platform_settings` → `site:*`).
 * Yalnız admin; organizatöre mount edilmez (platform ayarı).
 */
/** Admin GET: ayarlar + gerçek sayıların EŞİKSİZ hâli (okunamadıysa `null`). */
export type AdminSiteData = SiteSettings & { proofRaw: SiteProofRaw | null };

export const adminSiteApi = {
  get: () => request<AdminSiteData>("/api/admin/site", ""),
  put: <K extends SiteSection>(section: K, data: SiteSettings[K]) =>
    request<SiteSettings[K]>("/api/admin/site", `/${section}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteMedia: (kind: SiteMediaKind, key?: string) =>
    request<SiteMedia>("/api/admin/site", `/media/${kind}${key ? `?key=${encodeURIComponent(key)}` : ""}`, {
      method: "DELETE",
    }),
};

export type SiteMediaKind = "photo" | "video" | "poster";

/** Canlı demo düğünü (F7) — worker lib/demo.ts getDemoStatus. */
export interface DemoStatus {
  exists: boolean;
  isDemo: boolean;
  /** Slug gerçek bir düğüne ait → kurulum reddedilir, hiçbir şey silinmez. */
  slugTaken: boolean;
  visitors: number;
  seedMedia: number;
  pendingMedia: number;
}

/** Online satış siparişleri (F8b) — worker routes/admin-orders.ts. `available:false` = migration 053 yok. */
export const adminOrdersApi = {
  list: (opts: { status?: OrderStatus; cursor?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.cursor) q.set("cursor", opts.cursor);
    const qs = q.toString();
    return request<{ available: boolean; items: OrderRow[]; nextCursor: string | null }>("/api/admin/orders", qs ? `?${qs}` : "");
  },
  patch: (id: string, body: OrderPatch) =>
    request<{ order: OrderRow; organizerCreated: boolean | null; deactivated: boolean | null; emailSent: boolean | null }>(
      "/api/admin/orders",
      `/${id}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  /** "Ödendi" / "Taşı" için alıcının düğünleri. */
  weddings: (id: string) =>
    request<{ organizerExists: boolean; weddings: OrderWeddingOption[] }>("/api/admin/orders", `/${id}/weddings`),
  /** Teklifle (elle) sipariş — aynı tek etkinlik hak modeli. */
  create: (body: AdminOrderCreateInput) =>
    request<{ order: OrderRow; orders?: OrderRow[] }>("/api/admin/orders", "/", { method: "POST", body: JSON.stringify(body) }),
  /** İptal öncesi etki önizlemesi. */
  impact: (id: string) => request<OrderImpact>("/api/admin/orders", `/${id}/impact`),
  /** Defterdeki sözleşme kopyası (alıcının onayladığı metin + özet) ve olaylar. */
  ledger: (id: string) =>
    request<{ available: boolean; documents: { text: string; hash: string; entryHash: string; at: string } | null; events: SalesLedgerEntry[] }>(
      "/api/admin/orders",
      `/${id}/ledger`,
    ),
};

/** "Online satış → Ek paketler" — QR ekstra kayıt paketlerinin tahsilatı (worker routes/admin-addons.ts). */
export type AdminAddonRow = WeddingAddonRow & { wedding_slug: string; wedding_title: string };
export const adminAddonsApi = {
  list: (opts: { status?: WeddingAddonStatus; cursor?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.cursor) q.set("cursor", opts.cursor);
    const qs = q.toString();
    return request<{ available: boolean; items: AdminAddonRow[]; nextCursor: string | null }>("/api/admin/addons", qs ? `?${qs}` : "");
  },
  patch: (id: string, body: AddonAdminPatch) =>
    request<WeddingAddonRow>("/api/admin/addons", `/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
};

/** Satış kayıt defteri (migration 057) — yalnız okuma: fiyat/koşul geçmişi, sipariş olayları, bütünlük. */
export const adminLedgerApi = {
  list: (opts: { kind?: SalesLedgerKind; before?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.kind) q.set("kind", opts.kind);
    if (opts.before) q.set("before", String(opts.before));
    const qs = q.toString();
    return request<{ available: boolean; items: SalesLedgerEntry[]; nextCursor: number | null }>("/api/admin/ledger", qs ? `?${qs}` : "");
  },
  verify: () =>
    request<{ available: boolean; ok: boolean; count: number; brokenAt: number | null; head: string | null }>("/api/admin/ledger", "/verify"),
};

/** KVKK başvuru aracı (F9) — worker routes/admin-privacy.ts. */
export const adminPrivacyApi = {
  lookup: (phone: string) =>
    request<PrivacyLookup>("/api/admin/privacy", `/lookup?phone=${encodeURIComponent(phone)}`),
  erase: (phone: string) =>
    request<PrivacyEraseResult>("/api/admin/privacy", "/erase", { method: "POST", body: JSON.stringify({ phone }) }),
};

export const adminDemoApi = {
  status: () => request<DemoStatus>("/api/admin/demo", ""),
  setup: () => request<{ seeded: number; created: boolean; status: DemoStatus }>("/api/admin/demo", "/setup", { method: "POST" }),
  clearVisitors: () =>
    request<{ guests: number; media: number; status: DemoStatus }>("/api/admin/demo", "/clear-visitors", { method: "POST" }),
};

/**
 * Vitrin medyası yükleme — XHR (fetch yükleme ilerlemesi veremiyor; video
 * 95 MB'a kadar). Hata gövdesindeki kod (`too_large`, `mp4_only`, `image_only`,
 * `limit`) `Error.message` olarak döner → sayfa çevrilmiş mesaja eşler.
 */
export function uploadSiteMedia(
  kind: SiteMediaKind,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<SiteMedia> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/admin/site/media/${kind}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let json: ApiResponse<SiteMedia> | null = null;
      try {
        json = JSON.parse(xhr.responseText) as ApiResponse<SiteMedia>;
      } catch {
        /* gövde JSON değil (ör. 413 proxy sayfası) */
      }
      if (xhr.status >= 200 && xhr.status < 300 && json?.ok && json.data) resolve(json.data);
      else reject(new Error(json?.error ?? (xhr.status === 413 ? "too_large" : `HTTP ${xhr.status}`)));
    };
    xhr.onerror = () => reject(new Error("network"));
    if (kind === "video") {
      // Video HAM gövdeyle: worker belleğe almadan doğrudan R2'ye akıtır (95 MB'a kadar).
      xhr.setRequestHeader("Content-Type", "video/mp4");
      xhr.send(file);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  });
}

/** Teklif talepleri — migration 052 yoksa `available: false` (panel "migration bekleniyor" der). */
export type LeadsPage =
  | { available: false }
  | { available: true; items: ContactRequestRow[]; nextCursor: string | null; counts: Record<ContactRequestStatus, number> };
export type FunnelData = { available: false } | { available: true; days: FunnelDay[] };

export const adminLeadsApi = {
  list: (opts: { status?: ContactRequestStatus; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set("status", opts.status);
    if (opts.cursor) qs.set("cursor", opts.cursor);
    const query = qs.toString();
    return request<LeadsPage>("/api/admin/contact-requests", query ? `?${query}` : "");
  },
  funnel: (days = 30) => request<FunnelData>("/api/admin/contact-requests", `/funnel?days=${days}`),
  patch: (id: string, body: ContactRequestPatch) =>
    request("/api/admin/contact-requests", `/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) =>
    request("/api/admin/contact-requests", `/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

/** Onay kuyruğu satırı — worker `admin-sms-approvals.ts` GET / şekli. */
export interface SmsApprovalRow {
  id: string;
  wedding_id: string;
  name: string;
  message_template: string;
  segment_type: string;
  schedule_type: string;
  next_dispatch_at: string | null;
  approval_recipient_count: number | null;
  created_by: string;
  created_at: string;
  wedding_title: string;
  wedding_slug: string;
  organizer_email: string | null;
  organizer_name: string | null;
  segmentsPerMessage: number;
  estimatedSegments: number;
  /** Fail-closed sayımda alıcı sayısı bilinmez → maliyet de bilinmez (null). */
  estimatedCostUsd: number | null;
  messagePreview: string;
}
export interface SmsApprovalQueue {
  items: SmsApprovalRow[];
  nextCursor: string | null;
  threshold: number;
}

/** Çapraz-düğün kampanya satırı — worker `GET /campaigns` şekli. */
export interface SmsCampaignListRow {
  id: string;
  wedding_id: string;
  name: string;
  segment_type: string;
  schedule_type: string;
  status: string;
  hold_reason: string | null;
  approval_status: string | null;
  approval_recipient_count: number | null;
  rejection_reason: string | null;
  sends_used: number;
  rounds_completed: number;
  next_dispatch_at: string | null;
  last_dispatched_at: string | null;
  created_at: string;
  deleted_at: string | null;
  wedding_title: string;
  wedding_slug: string;
  organizer_email: string | null;
  organizer_name: string | null;
  /** Oluşturulmuş `sms_sends` satırı sayısı (alıcı × tur). */
  sends_total: number;
  /** GERÇEKLEŞEN segment — faturaya esas birim (`sms_sends.segments`, 040). */
  segments_used: number;
  delivered_count: number;
  failed_count: number;
  /** `segments_used × birim fiyat` — tahmin değil, olan biten. */
  spentUsd: number;
}
export interface SmsCampaignFilters {
  status?: string;
  /** `none` = onay sürecine hiç girmemiş (kolon NULL). */
  approval?: string;
  weddingId?: string;
  includeDeleted?: boolean;
}
export interface SmsCampaignList {
  items: SmsCampaignListRow[];
  nextCursor: string | null;
  priceUsd: number;
}
export interface SmsCampaignWeddingOption {
  id: string;
  title: string;
  slug: string;
  campaign_count: number;
}

/**
 * Admin SMS onay kuyruğu. Worker tarafında TEK mount'lu, admin-only router
 * (`/api/admin/sms-approvals`) — organizer panelinde karşılığı YOKTUR.
 */
export const adminSmsApprovalsApi = {
  list: (cursor?: string) =>
    request<SmsApprovalQueue>(
      "/api/admin/sms-approvals",
      cursor ? `/?cursor=${encodeURIComponent(cursor)}` : "",
    ),
  listAll: (filters: SmsCampaignFilters = {}, cursor?: string) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.set("status", filters.status);
    if (filters.approval) qs.set("approval", filters.approval);
    if (filters.weddingId) qs.set("weddingId", filters.weddingId);
    if (filters.includeDeleted) qs.set("includeDeleted", "1");
    if (cursor) qs.set("cursor", cursor);
    const q = qs.toString();
    return request<SmsCampaignList>("/api/admin/sms-approvals", `/campaigns${q ? `?${q}` : ""}`);
  },
  weddings: () =>
    request<{ items: SmsCampaignWeddingOption[] }>("/api/admin/sms-approvals", "/weddings"),
  approve: (id: string) =>
    request<{ id: string; dispatchTriggered: boolean }>(
      "/api/admin/sms-approvals", `/${id}/approve`, { method: "POST" },
    ),
  reject: (id: string, reason?: string) =>
    request<{ id: string }>(
      "/api/admin/sms-approvals", `/${id}/reject`,
      { method: "POST", body: JSON.stringify(reason ? { reason } : {}) },
    ),
};

export interface AdminSupportMessage {
  id: string;
  author_type: "organizer" | "admin";
  content: string;
  is_internal: number; // 0 or 1
  attachment_keys: string | null;
  created_at: string;
}

/** Admin support tickets API */
export const adminSupportApi = (() => {
  const base = `/api/admin/support`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) =>
      request<T>(base, path, { method: "DELETE" }),
    resolve: (id: string, content?: string, sendNotification = true) =>
      request<void>(base, `/${id}/resolve`, {
        method: "POST", body: JSON.stringify({ content, sendNotification }),
      }),
    listCanned: () =>
      request<Array<{ id: string; title: string; content: string; position: number }>>(base, "/canned"),
    createCanned: (title: string, content: string) =>
      request<{ id: string }>(base, "/canned", {
        method: "POST", body: JSON.stringify({ title, content }),
      }),
    deleteCanned: (id: string) =>
      request<void>(base, `/canned/${id}`, { method: "DELETE" }),
    uploadAttachments: async (ticketId: string, files: File[]): Promise<string[]> => {
      const fd = new FormData();
      files.forEach((f) => fd.append("file", f));
      const res = await fetch(`/api/admin/support/${ticketId}/attachments`, {
        method: "POST", credentials: "include", body: fd,
      });
      const json = await res.json() as { ok: boolean; data?: { keys: string[] }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data?.keys ?? [];
    },
    attachmentUrl: (ticketId: string, key: string) =>
      `/api/admin/support/${ticketId}/attachments/${key}`,
  };
})();

/** Admin themes API */
export const adminThemesApi = {
  getBackgrounds: () =>
    request<Array<{ slot: string; url: string | null }>>("/api/admin/themes", "/backgrounds"),
  uploadBackground: async (slot: string, file: File): Promise<{ url: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/admin/themes/backgrounds/${slot}`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    const json = await res.json() as { ok: boolean; data?: { url: string }; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Upload failed");
    return json.data!;
  },
  deleteBackground: (slot: string) =>
    request<void>("/api/admin/themes", `/backgrounds/${slot}`, { method: "DELETE" }),
};

export interface AlbumSummary {
  id: string;
  kind: "favorites" | "custom";
  name: string;
  cover_media_id: string | null;
  cover_r2_key: string | null;
  position: number;
  is_public: number;
  created_at: string;
  item_count: number;
}

export interface AlbumItem {
  media_id: string;
  r2_key: string;
  kind: "image" | "video";
  content_type: string;
  created_at: string;
  position: number;
  added_at: string;
}

export interface GalleryMedia {
  id: string;
  kind: "image" | "video";
  r2_key: string;
  content_type: string;
  bytes: number;
  created_at: string;
  guest_name: string | null;
  guest_phone: string | null;
  album_ids: string[];
  is_favorite: boolean;
  like_count: number;
  comment_count: number;
}

export interface MediaComment {
  id: string;
  content: string;
  author_name: string;
  guest_id: string;
  created_at: string;
  is_hidden: boolean;
}

export function createAdminMediaSocialApi(slug: string) {
  const base = `/api/admin/w/${slug}`;
  return {
    getComments: async (mediaId: string, cursor?: string): Promise<{
      comments: MediaComment[]; hasMore: boolean; like_count: number; comment_count: number;
    }> => {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const res = await fetch(`${base}/media/${mediaId}/comments${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load comments: ${res.status}`);
      const json = await res.json() as { ok: boolean; data: MediaComment[]; hasMore: boolean; like_count: number; comment_count: number };
      return { comments: json.data ?? [], hasMore: json.hasMore ?? false, like_count: json.like_count ?? 0, comment_count: json.comment_count ?? 0 };
    },
    deleteComment: async (mediaId: string, commentId: string): Promise<void> => {
      const res = await fetch(`${base}/media/${mediaId}/comments/${commentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    toggleCommentVisibility: async (mediaId: string, commentId: string, isHidden: boolean): Promise<void> => {
      const res = await fetch(`${base}/media/${mediaId}/comments/${commentId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_hidden: isHidden }),
      });
      if (!res.ok) throw new Error(`Toggle failed: ${res.status}`);
    },
  };
}

export const createAdminAlbumApi = (slug: string) => {
  const base = `/api/admin/w/${slug}`;
  return {
    listAlbums: () =>
      request<AlbumSummary[]>(base, "/albums"),
    createAlbum: (name: string) =>
      request<{ id: string }>(base, "/albums", { method: "POST", body: JSON.stringify({ name }) }),
    renameAlbum: (id: string, name: string) =>
      request<void>(base, `/albums/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    deleteAlbum: (id: string) =>
      request<void>(base, `/albums/${id}`, { method: "DELETE" }),
    getAlbumItems: (id: string, cursor?: string, sort?: "desc" | "asc"): Promise<{ ok: boolean; data: AlbumItem[]; hasMore: boolean; error?: string }> => {
      const p = new URLSearchParams();
      if (cursor) p.set("cursor", cursor);
      if (sort) p.set("sort", sort);
      const qs = p.toString() ? `?${p}` : "";
      return request<AlbumItem[]>(base, `/albums/${id}/items${qs}`) as Promise<{ ok: boolean; data: AlbumItem[]; hasMore: boolean; error?: string }>;
    },
    addItem: (albumId: string, mediaId: string) =>
      request<void>(base, `/albums/${albumId}/items`, { method: "POST", body: JSON.stringify({ media_id: mediaId }) }),
    removeItem: (albumId: string, mediaId: string) =>
      request<void>(base, `/albums/${albumId}/items/${mediaId}`, { method: "DELETE" }),
    setCover: (albumId: string, mediaId: string | null) =>
      request<void>(base, `/albums/${albumId}/cover`, { method: "PATCH", body: JSON.stringify({ media_id: mediaId }) }),
    setPublic: (id: string, isPublic: boolean) =>
      request<void>(base, `/albums/${id}`, { method: "PATCH", body: JSON.stringify({ is_public: isPublic }) }),
    toggleFavorite: (mediaId: string) =>
      request<{ favorited: boolean }>(base, `/media/${mediaId}/favorite`, { method: "POST" }),
    getGallery: (cursor?: string, filters?: { kind?: "image" | "video"; search?: string }) => {
      const p = new URLSearchParams();
      if (cursor) p.set("cursor", cursor);
      if (filters?.kind) p.set("kind", filters.kind);
      if (filters?.search) p.set("search", filters.search);
      const qs = p.toString() ? `?${p}` : "";
      return request<{ media: GalleryMedia[]; hasMore: boolean }>(base, `/media/gallery${qs}`);
    },
  };
};
